import React from 'react';
import { Link } from 'react-router-dom';


/* ─── Dashboard Screenshot ──────────────────────────────────────────────── */
const DashboardShot: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <div style={{
    width: '100%',
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: '0 -2px 0 0 rgba(255,255,255,.06), 0 24px 60px -8px rgba(0,0,0,.5)',
  }}>
    <img src={src} alt={alt} style={{ width: '100%', display: 'block' }} />
  </div>
);

/* ─── Pill Tag ──────────────────────────────────────────────────────────── */
const Tag: React.FC<{ children: React.ReactNode; variant?: 'red' | 'light' | 'dark' }> = ({ children, variant = 'red' }) => {
  const styles = {
    red:   { background: 'rgba(151,0,0,.1)',  color: '#970000' },
    light: { background: 'rgba(255,255,255,.2)', color: '#fff' },
    dark:  { background: 'rgba(26,6,6,.08)', color: 'rgba(26,6,6,.7)' },
  }[variant];
  return (
    <span style={{
      display:'inline-flex', alignSelf:'flex-start', alignItems:'center', gap:5,
      fontSize:10, fontWeight:800, letterSpacing:'0.07em', textTransform:'uppercase',
      padding:'5px 11px', borderRadius:999, marginBottom:11,
      ...styles,
    }}>
      {children}
    </span>
  );
};

/* ─── Hero ───────────────────────────────────────────────────────────────── */
const Hero = () => {
  return (
    <section id="home" style={{ background:'#fff', position:'relative' }}>
      {/* Subtle grid */}
      <div style={{ pointerEvents:'none', position:'absolute', inset:0, zIndex:0 }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(to right,rgba(26,6,6,.06) 1px,transparent 1px),linear-gradient(to bottom,rgba(26,6,6,.06) 1px,transparent 1px)', backgroundSize:'48px 48px', opacity:.5 }} />
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom,#fff,rgba(255,255,255,.5),#fff)' }} />
      </div>

      <div style={{ maxWidth:1300, margin:'0 auto', padding:'80px 32px 0', position:'relative', zIndex:1 }}>
        <div
          style={{ display:'grid', gridTemplateColumns:'0.85fr 1.15fr', gap:52, alignItems:'start' }}
          className="hero-grid"
        >

          {/* ── LEFT: sticky copy ── */}
          <div style={{ position:'sticky', top:48, alignSelf:'start', paddingTop:4, paddingBottom:80 }}>

            {/* Label */}
            <p style={{
              fontSize:10, fontWeight:800, letterSpacing:'0.12em', textTransform:'uppercase',
              color:'#970000', margin:'0 0 18px',
            }}>
              Arabisch lernen online
            </p>

            {/* Headline */}
            <h1 style={{
              fontFamily:'"Plus Jakarta Sans",sans-serif', fontWeight:800,
              letterSpacing:'-.03em', lineHeight:1.02, margin:0,
              fontSize:'clamp(38px,4.2vw,58px)', color:'#1a0606',
            }}>
              Arabisch lernen –{' '}
              <br />
              <em style={{ fontStyle:'italic', color:'#970000' }}>der richtige Weg</em>
              <br />von Anfang an.
            </h1>

            <p style={{ color:'rgba(26,6,6,.65)', fontSize:16.5, lineHeight:1.65, maxWidth:'40ch', margin:'22px 0 0' }}>
              Strukturierte Live-Kurse auf Deutsch, kleine Gruppen, bewährte Lehrwerke –
              vom arabischen Alphabet bis zum selbstständigen Lesen. Online, flexibel, mit echter Begleitung.
            </p>

            {/* CTAs */}
            <div style={{ display:'flex', gap:11, flexWrap:'wrap', marginTop:28 }}>
              <Link to="/anmelden" style={{
                display:'inline-flex', alignItems:'center', gap:8,
                padding:'13px 24px', borderRadius:999, fontWeight:700, fontSize:14.5,
                background:'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15)), #b30000', color:'#fff', textDecoration:'none',
                boxShadow:'0 8px 20px -8px rgba(151,0,0,.35)',
                transition:'filter .15s, transform .15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.filter='brightness(1.1)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.filter=''; e.currentTarget.style.transform='translateY(0)'; }}
              >
                ▶ Jetzt anmelden
              </Link>
              <a href="#contact" style={{
                display:'inline-flex', alignItems:'center', gap:8,
                padding:'13px 24px', borderRadius:999, fontWeight:700, fontSize:14.5,
                border:'1.5px solid rgba(26,6,6,.18)', color:'#1a0606',
                background:'rgba(255,255,255,.7)', textDecoration:'none', transition:'all .15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='#970000'; e.currentTarget.style.color='#970000'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(26,6,6,.18)'; e.currentTarget.style.color='#1a0606'; }}
              >
                Kostenlose Beratung →
              </a>
            </div>

          </div>

          {/* ── RIGHT: scrollable bento ── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14, paddingBottom:80 }}>

            {/* Card 1 — App Flashcard */}
            <div style={{
              borderRadius:24,
              overflow:'hidden',
              display:'flex', flexDirection:'column',
              background:'#fff',
              border:'1.5px solid rgba(151,0,0,.18)',
              boxShadow:'0 4px 24px -8px rgba(26,6,6,.10)',
              height: 420,
            }}>
              {/* Text */}
              <div style={{ padding:'24px 22px 14px', flexShrink:0 }}>
                <Tag variant="red">Lern-App</Tag>
                <h3 style={{ fontFamily:'"Plus Jakarta Sans",sans-serif', fontWeight:800, letterSpacing:'-.02em', margin:0, lineHeight:1.15, fontSize:17, color:'#1a0606' }}>
                  Lerne die Vokabeln einfach mit unserer App.
                </h3>
                <p style={{ fontSize:12, color:'rgba(26,6,6,.5)', marginTop:7, lineHeight:1.55, maxWidth:'24ch' }}>
                  Aktuell nur für unsere Schüler zugänglich.
                </p>
              </div>
              {/* Mockup — fill remaining space, zoom deep into phone */}
              <div style={{
                flex: 1,
                backgroundImage: 'url(/app-mockup.png)',
                backgroundSize: '230%',
                backgroundPosition: 'center 30%',
                backgroundRepeat: 'no-repeat',
              }} />
            </div>

            {/* Card 2 — Beratung CTA */}
            <div style={{
              borderRadius:24, padding:'26px 22px',
              display:'flex', flexDirection:'column', justifyContent:'space-between',
              background:'#b30000',
              border:'1px solid rgba(255,255,255,.08)',
              boxShadow:'0 4px 24px -8px rgba(151,0,0,.4)',
              position:'relative', overflow:'hidden',
              height: 420,
            }}>
              {/* Grid overlay */}
              <div style={{ pointerEvents:'none', position:'absolute', inset:0, background:'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15))' }} />
              <div style={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', justifyContent:'space-between', flex:1 }}>
              <Tag variant="light">Kostenlos</Tag>
              <div>
                <div style={{ fontSize:22, fontWeight:800, color:'#fff', fontFamily:'"Plus Jakarta Sans",sans-serif', lineHeight:1.2, letterSpacing:'-.02em' }}>
                  Nicht sicher welcher Kurs zu dir passt?
                </div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,.65)', marginTop:10, lineHeight:1.6 }}>
                  Melde dich für ein kostenloses Beratungsgespräch — wir finden den richtigen Einstieg für dich.
                </div>
              </div>
              <a href="#contact" style={{
                marginTop:20, alignSelf:'flex-start',
                background:'#fff', color:'#970000',
                fontWeight:800, fontSize:13,
                padding:'10px 18px', borderRadius:999,
                display:'inline-flex', gap:6, textDecoration:'none',
                transition:'background .15s, transform .15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background='#f5f5f5'; e.currentTarget.style.transform='translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='#fff'; e.currentTarget.style.transform=''; }}
              >
                Beratung anfragen →
              </a>
              </div>
            </div>

            {/* Card 3 — Wörterbuch (span 2) */}
            <div style={{
              gridColumn: 'span 2',
              borderRadius:24,
              overflow:'hidden',
              display:'flex',
              background:'#fff',
              border:'1.5px solid rgba(151,0,0,.18)',
              boxShadow:'0 4px 24px -8px rgba(26,6,6,.10)',
              height: 220,
            }}>
              {/* Text left */}
              <div style={{ padding:'24px 22px', flexShrink:0, display:'flex', flexDirection:'column', justifyContent:'center', flex:1 }}>
                <Tag variant="red">Wörterbuch</Tag>
                <h3 style={{ fontFamily:'"Plus Jakarta Sans",sans-serif', fontWeight:800, letterSpacing:'-.02em', margin:0, lineHeight:1.15, fontSize:16, color:'#1a0606' }}>
                  Wörter nachschlagen — jederzeit.
                </h3>
                <p style={{ fontSize:12, color:'rgba(26,6,6,.5)', marginTop:6, lineHeight:1.55, maxWidth:'22ch' }}>
                  Konjugationen, Formen & Bedeutungen auf einen Blick.
                </p>
              </div>
              {/* Mockup right */}
              <div style={{
                width: '50%',
                flexShrink: 0,
                backgroundImage: 'url(/app-dictionary.png)',
                backgroundSize: '220%',
                backgroundPosition: 'center 50%',
                backgroundRepeat: 'no-repeat',
              }} />
            </div>

            {/* Card 3 — Schülerbereich browser mockup, span 2 */}
            <div style={{
              gridColumn:'span 2',
              borderRadius:24, padding:'26px 26px 0',
              display:'flex', flexDirection:'column',
              background:'#b30000',
              border:'1px solid rgba(255,255,255,.08)',
              boxShadow:'0 4px 28px -8px rgba(26,6,6,.3)',
              minHeight:320,
              position:'relative', overflow:'hidden',
            }}>
              <div style={{ pointerEvents:'none', position:'absolute', inset:0, background:'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15))' }} />
              <div style={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', flex:1 }}>
              <div style={{ marginBottom:20 }}>
                <Tag variant="light">Schülerbereich</Tag>
                <h3 style={{ fontFamily:'"Plus Jakarta Sans",sans-serif', fontWeight:800, letterSpacing:'-.02em', margin:0, lineHeight:1.15, fontSize:21, color:'#fff', maxWidth:'30ch' }}>
                  Alles zu deinem Kurs an einem Ort.
                </h3>
                <p style={{ color:'rgba(255,255,255,.6)', fontSize:13, marginTop:8, lineHeight:1.55, maxWidth:'38ch' }}>
                  Aufzeichnungen, Live-Termine, Materialien, Fortschritt &amp; Zertifikate.
                </p>
              </div>
              <div style={{ marginTop:'auto', transform:'translateY(32px)' }}>
                <DashboardShot src="/Dashboard.png" alt="Arabicroots Schülerbereich – Kursübersicht" />
              </div>
              </div>
            </div>


          </div>
        </div>
      </div>

      <style>{`
        @keyframes ping {
          75%, 100% { transform: scale(2.2); opacity: 0; }
        }
        .pulse-ring { animation: ping 1.8s cubic-bezier(0,0,.2,1) infinite; }

        @media (max-width: 960px) {
          .hero-grid { grid-template-columns: 1fr !important; gap: 36px !important; }
          .hero-grid > div:first-child { position: static !important; padding-bottom: 0 !important; }
        }
        @media (max-width: 600px) {
          .hero-grid > div:last-child { grid-template-columns: 1fr !important; }
          .hero-grid > div:last-child > div[style*="span 2"] { grid-column: 1 !important; }
          .hero-grid > div:last-child > div[style*="row"] { grid-row: auto !important; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
